import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import MultiSelectDropdown from "./common/MultiSelectDropdown";
import { getEvents, uploadNotificationImage } from "../api";
import { buildBulkEmailPreviewHtml } from "../templates/bulkEmailTemplate";
import resizeFile from "../utils/resizeFile";
import readStreamAsBase64 from "../utils/readStreamAsBase64";
import config from "../config";

const getThisWeekRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { fromDate: fmt(monday), toDate: fmt(sunday) };
};

const formatEventLabel = (event) => {
  const date = event.date
    ? new Date(event.date).toLocaleDateString([], {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;
  const parts = [event.name, date && `(${date})`, event.venueName && `@ ${event.venueName}`];
  return parts.filter(Boolean).join(" ");
};

export default function EmailModal(props) {
  const titleInputRef = useRef();
  const heroImageInputRef = useRef();
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventOptions, setEventOptions] = useState(null);
  const [allWeekEvents, setAllWeekEvents] = useState([]);
  const [step, setStep] = useState("form");
  const [preview, setPreview] = useState(null);
  const [state, setState] = useState({});
  const [heroImage, setHeroImage] = useState(null); // { id, previewSrc }
  const [heroImageUploading, setHeroImageUploading] = useState(false);

  useEffect(() => {
    const src = heroImage?.previewSrc;
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [heroImage]);

  useEffect(() => {
    const { fromDate: weekStart, toDate: weekEnd } = getThisWeekRange();
    const today = new Date();
    const todayStr =
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    getEvents({ fromDate: weekStart, toDate: weekEnd, limit: 0 }).then((events) => {
      const list = events || [];
      setAllWeekEvents(list);
      setEventOptions(
        list
          .filter((e) => !e.date || e.date >= todayStr)
          .map((e) => ({
            id: e.id,
            label: formatEventLabel(e),
            name: e.name,
            date: e.date,
            venueName: e.venueName,
          }))
      );
    });
  }, []);

  const handleHeroImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setHeroImageUploading(true);
    setState((s) => ({ ...s, errors: { ...s.errors, heroImage: undefined } }));
    try {
      const resized = await resizeFile(file);
      const base64 = await readStreamAsBase64(resized);
      const id = crypto.randomUUID();
      await uploadNotificationImage(id, base64);
      setHeroImage({ id, previewSrc: URL.createObjectURL(file) });
    } catch (err) {
      console.error("Failed to upload hero image", err);
      setState((s) => ({ ...s, errors: { ...s.errors, heroImage: "Image upload failed. Please try again." } }));
    } finally {
      setHeroImageUploading(false);
    }
  };

  const handlePreview = () => {
    const validationErrors = {};

    if (!titleInputRef.current.value) {
      validationErrors.title = "Title is required.";
    }

    if (!selectedEvents.length) {
      validationErrors.events = "Please select at least one event.";
    }

    if (Object.keys(validationErrors).length) {
      setState((s) => ({ ...s, errors: validationErrors }));
      return;
    }

    const today = new Date();
    const todayStr =
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const selectedIds = new Set(selectedEvents.map((e) => e.id));
    const moreEvents = allWeekEvents
      .filter((e) => !selectedIds.has(e.id) && (!e.date || e.date >= todayStr))
      .sort((a, b) => (!a.date ? 1 : !b.date ? -1 : a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    setState((s) => ({ ...s, errors: undefined }));
    setPreview({
      title: titleInputRef.current.value,
      selectedEvents,
      moreEvents,
      heroImageId: heroImage?.id,
    });
    setStep("preview");
  };

  const handleSend = async () => {
    setState((s) => ({ ...s, isLoading: true, errors: undefined }));
    const body = {
      userId: props.user.id,
      emailTitle: preview.title,
      selectedEventIds: preview.selectedEvents.map((e) => e.id),
      heroImageId: preview.heroImageId,
    };
    try {
      await props.handleConfirm(body);
      props.handleClose();
    } catch (error) {
      setState((s) => ({
        ...s,
        isLoading: false,
        errors: {
          submit: error?.message || error || "Failed to send email.",
        },
      }));
    }
  };

  return (
    <Dialog
      open={true}
      onClose={props.handleClose}
      fullWidth
      maxWidth={step === "preview" ? "md" : "sm"}
      PaperProps={{ style: { backgroundColor: "#d8c8f4", color: "#4b445b" } }}
    >
      {step === "form" ? (
        <>
          <DialogTitle>Send Bulk Email</DialogTitle>
          <DialogContent>
            <TextField
              autoComplete="off"
              margin="dense"
              id="title"
              label="Title"
              type="text"
              fullWidth
              variant="outlined"
              inputRef={titleInputRef}
              defaultValue={preview?.title}
              required
              error={!!state.errors?.title}
              helperText={state.errors?.title}
            />
            <MultiSelectDropdown
              id="email-featured-events"
              label="Featured Events"
              options={eventOptions || []}
              value={selectedEvents}
              onChange={setSelectedEvents}
              placeholder={
                eventOptions === null
                  ? "Loading events…"
                  : eventOptions.length
                  ? "Select featured events for this week"
                  : "No events this week"
              }
            />
            {!!state.errors?.events && (
              <Typography variant="caption" color="error" sx={{ display: "block", marginTop: 1 }}>
                {state.errors.events}
              </Typography>
            )}
            <input
              ref={heroImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleHeroImageChange}
            />
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => heroImageInputRef.current.click()}
                disabled={heroImageUploading}
              >
                {heroImageUploading ? "Uploading…" : heroImage ? "Change Hero Image" : "Upload Hero Image"}
              </Button>
              {heroImage && (
                <img
                  src={heroImage.previewSrc}
                  alt="Hero preview"
                  style={{ height: 48, width: 90, objectFit: "cover", borderRadius: 4 }}
                />
              )}
            </div>
            {!!state.errors?.heroImage && (
              <Typography variant="caption" color="error" sx={{ display: "block", marginTop: 0.5 }}>
                {state.errors.heroImage}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={props.handleClose} variant="contained">
              Cancel
            </Button>
            <Button onClick={handlePreview} variant="contained" disabled={heroImageUploading}>
              Preview
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            Email Preview
            <Typography variant="caption" sx={{ display: "block", color: "#6b6478", mt: 0.5 }}>
              {preview.title}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <iframe
              title="Email Preview"
              srcDoc={buildBulkEmailPreviewHtml({
                subject: preview.title,
                selectedEvents: preview.selectedEvents,
                moreEvents: preview.moreEvents,
                heroImageUrl: preview.heroImageId ? `${config.cdnUrl}/notifications/${preview.heroImageId}` : undefined,
              })}
              sandbox="allow-same-origin"
              style={{ width: "100%", height: 520, border: "none", display: "block" }}
            />
            {!!state.errors?.submit && (
              <Typography variant="body2" color="error" sx={{ px: 3, pb: 1 }}>
                {state.errors.submit}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep("form")} variant="contained" disabled={state.isLoading}>
              Back
            </Button>
            <Button onClick={handleSend} variant="contained" disabled={state.isLoading}>
              Confirm & Send
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
